import AIState, { useAIState } from "@/components/AIState";
import VideoAttribution from "@/components/VideoAttribution";
import MediaImage from "@/components/MediaImage";
import VideoPreview from "@/components/VideoPreview";
import { originalVideoLink } from "@/lib/media";
import { useRead, ReadState, isVideos } from "@/components/LoadingStates";
import LiveDemoBadge, { isLiveCreator } from "@/components/LiveDemoBadge";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatError, mutationFailure } from "@/lib/api";
import PlatformBadge from "@/components/PlatformBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import VideoAnalysis, { AnalysisEvidenceNotice } from "@/components/VideoAnalysis";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatNumber, timeAgo } from "@/lib/format";
import { ArrowLeft, Bookmark, BookmarkCheck, Eye, Heart, MessageCircle, Share2, Sparkles, ExternalLink, MessagesSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CommentThread from "@/components/CommentThread";
import ConfirmAction from "@/components/ConfirmAction";
import { focusIfShown, focusTemporarily } from "@/lib/focus";

export default function VideoDetail() {
  const { canEdit, user } = useAuth();
  const { id } = useParams();
  const ai = useAIState("video_manual", id);
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const detailRead = useRead(`/videos/${id}${searchParams.get("profile") ? `?${new URLSearchParams({ profile: searchParams.get("profile") })}` : ""}`, (d) => d && isVideos([d.video]));
  const data = detailRead.data;
  const setData = detailRead.setData;
  const currentDetailKey = useRef(detailRead.key);
  currentDetailKey.current = detailRead.key;
  const [analysisState, setAnalysisState] = useState(null);
  const analysis = analysisState?.key === detailRead.key ? analysisState.data : null;
  const setAnalysis = useCallback((value) => { if (currentDetailKey.current === detailRead.key) setAnalysisState({ key: detailRead.key, data: value }); }, [detailRead.key]);
  const updateCommentCount = useCallback((count) => { if (currentDetailKey.current === detailRead.key) setCommentCount(count); }, [detailRead.key]);
  const payloadRef = useRef(data); payloadRef.current = data;
  const [analysisAuto, setAnalysisAuto] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  // OP-026 (REV-SAVED-01). Three texts are kept apart: `text`, what the editor holds now; `base` and
  // `revision`, the stored note this draft started from (last read or confirmed write); and the text sent
  // by a write, kept in the issue panel. `edited` records that the user typed in the editor since it was
  // last filled by a read or confirmed by a write; comparing texts cannot tell, since the user may have
  // typed their way back to the stored text (even an empty one) while a write was uncertain. A read
  // replaces the editor only when `edited` is false. A write clears `edited` only if confirmed with no
  // keystroke since it was sent (`seq`). Only an explicit "Relire la fiche" bases a local edit on the
  // note just read. Nothing is ever resent automatically.
  const [noteDraft, setNoteDraft] = useState({ key: null, text: "", base: "", revision: undefined, edited: false, seq: 0 });
  const rebaseOnRead = useRef(false);
  const note = noteDraft.key === detailRead.key ? noteDraft.text : "";
  const unsent = noteDraft.key === detailRead.key && noteDraft.edited && noteDraft.text !== noteDraft.base;
  const setNote = (text) => setNoteDraft((prev) => ({ ...prev, text, edited: true, seq: prev.seq + 1 }));
  // `sentSeq`: keystroke count when the confirmed write was sent; omitted when nothing the editor sent was confirmed.
  const confirmNote = (key, base, revision, sentSeq) => setNoteDraft((prev) => (prev.key !== key ? prev
    : { ...prev, base, revision, edited: sentSeq === undefined || prev.seq !== sentSeq ? prev.edited : false }));
  const [savedIssue, setSavedIssue] = useState(null);
  const noteDraftRef = useRef(noteDraft); noteDraftRef.current = noteDraft;
  const savedIssueRef = useRef(savedIssue); savedIssueRef.current = savedIssue;
  // OP-036. `removal`: the confirmation dialog, with the exact Saved state it describes (`snapshot`, whose
  // revisions the DELETE sends) and what the thread re-read found. `localCopy`: after a removal is known to have
  // happened, the last note and tags this page read, shown read-only and never sent; it is a third text, distinct
  // from the draft and from the server-confirmed note. Both belong to one read key and vanish on any context change.
  const [removal, setRemoval] = useState(null);
  const [localCopy, setLocalCopy] = useState(null);
  const removalSeq = useRef(0);
  // F-NAV-1: where focus goes when the control that opened the removal dialog is unknown or gone: the Save/Saved
  // button of this page if shown, else the page content.
  const saveButton = useRef(null);
  const page = useRef(null);
  const removalFocusFallback = () => { if (!focusIfShown(saveButton.current) && page.current?.isConnected) focusTemporarily(page.current); };
  const [commentCount, setCommentCount] = useState(0);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const data = payloadRef.current;
    const stored = data?.saved_meta?.note || "";
    const revision = data?.saved_meta?.revisions?.note;
    const rebase = rebaseOnRead.current;
    rebaseOnRead.current = false;
    setNoteDraft((prev) => {
      if (prev.key !== detailRead.key) return { key: detailRead.key, text: stored, base: stored, revision, edited: false, seq: 0 };
      if (!prev.edited) return { ...prev, text: stored, base: stored, revision };
      return rebase ? { ...prev, base: stored, revision } : prev;
    });
    setAnalysis(data?.analysis || null);
    setAnalysisAuto(!!data?.analysis_auto);
    setCommentCount(data?.comment_count || 0);
    setUnread(data?.unread_comments || 0);
  }, [detailRead.key, detailRead.readVersion, setAnalysis]);

  useEffect(() => {
    setRemoval((r) => (r && r.key === detailRead.key ? r : null));
    setLocalCopy((c) => (c && c.key === detailRead.key ? c : null));
  }, [detailRead.key]);

  const runAnalysis = async () => {
    if (!canEdit || analyzing || !ai.allowed || detailRead.status !== "success") return;
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/videos/${id}/analyze`);
      setAnalysis(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setAnalyzing(false);
      ai.read.reload().catch(() => {});
    }
  };

  // Late answers: React state is guarded by the read key; messages are dropped once the page shows
  // another video, account, workspace or role, so no outcome is announced in a context it did not touch.
  // `sent` is the note text a failed write carried (undefined for a removal); `removed` the snapshot of a removal.
  const failed = (err, key, sent, removed) => {
    if (currentDetailKey.current !== key) return;
    const kind = mutationFailure(err);
    const detail = formatError(err);
    if (removed && err?.response?.data?.detail?.code === "saved_missing") {
      // Already gone: by whom, and in which version, is unknown. The copy is only the last note this page read.
      const kept = noteDraftRef.current.key === key && noteDraftRef.current.edited;
      setData((prev) => ({ ...prev, saved: false, saved_meta: null }));
      afterRemoval(key, removed);
      setSavedIssue({ key, message: `${detail}${kept ? " Votre brouillon reste dans l’éditeur ; « Save » créerait une nouvelle référence." : ""}${hasCopy(removed) ? " La dernière note lue par cette page est conservée dans « My Notes », en copie locale non enregistrée." : ""}` });
    } else if (err?.response?.data?.detail?.code === "saved_missing") {
      setData((prev) => ({ ...prev, saved: false, saved_meta: null }));
      confirmNote(key, "", undefined);
      setSavedIssue({ key, sent, message: `${detail} Votre note reste dans l’éditeur ; « Save » créerait une nouvelle référence.` });
    } else if (kind === "conflict" || kind === "unknown") {
      // An uncertain removal keeps its snapshot: if the re-read finds no reference, it becomes the local copy.
      setSavedIssue({ key, sent, canReread: true, ...(removed && kind === "unknown" ? { removal: removed } : {}), message: kind === "conflict" ? `${detail} Relisez la fiche avant de décider.`
        : `Résultat inconnu (${detail}) : l’opération a peut-être eu lieu. Relisez la fiche avant de réessayer.` });
    }
    toast.error(kind === "unknown" ? `Résultat inconnu : ${detail}` : detail);
  };

  // Creation only: never replaces a reference saved meanwhile (409), and sends no tags.
  const create = async (key) => {
    const sent = note;
    const sentSeq = noteDraft.seq;
    try {
      const { data: answer } = await api.post("/saved", { video_id: id, expected_absent: true, ...(sent ? { note: sent } : {}) });
      if (currentDetailKey.current !== key) return;
      setSavedIssue(null);
      if (!answer?.saved_meta) { confirmNote(key, sent, undefined, sentSeq); detailRead.reload().catch(() => {}); return; }
      setData((prev) => ({ ...prev, saved: true, saved_meta: answer.saved_meta }));
      confirmNote(key, answer.saved_meta.note ?? sent, answer.saved_meta.revisions?.note, sentSeq);
      toast.success("Saved to library");
    } catch (err) {
      failed(err, key, sent);
    }
  };

  const hasCopy = (removed) => !!(removed.note || removed.tags.length);
  // A removal is known to have happened (200, or 404 for a removal): the editor is emptied only if nothing was
  // typed since it was last filled (`edited` false), so "Save" cannot send the removed note back; any typed text,
  // even the original or an empty one, stays as the draft. The last note read becomes the local copy.
  const afterRemoval = (key, removed) => {
    setNoteDraft((prev) => (prev.key !== key ? prev : prev.edited ? { ...prev, base: "", revision: undefined } : { ...prev, text: "", base: "", revision: undefined }));
    setLocalCopy(hasCopy(removed) ? { key, note: removed.note, tags: removed.tags } : null);
  };

  // The "Saved" button opens the confirmation; nothing is sent before "Retirer de Saved". The dialog describes the
  // Saved state read by this page (`snapshot`) and the DELETE sends exactly its two revisions (REV-SAVED-02), so a
  // reference edited or saved again since is not removed (409).
  const toggleSave = () => {
    if (!canEdit || !data || detailRead.status !== "success") return;
    const key = detailRead.key;
    if (!data.saved) return create(key);
    const m = data.saved_meta || {};
    const token = ++removalSeq.current;
    const snapshot = { note: m.note || "", tags: Array.isArray(m.tags) ? m.tags : [], revisions: m.revisions || {}, savedBy: m.saved_by, savedBasis: m.saved_basis, editedBy: m.edited_by };
    const update = (part) => setRemoval((r) => (r && r.token === token ? { ...r, ...part } : r));
    setRemoval({ key, token, snapshot, counts: "loading", workspace: user?.workspace_id ? "loading" : null });
    api.get(`/videos/${encodeURIComponent(id)}/comments`).then(({ data: t }) => {
      if (!t || !Array.isArray(t.items) || !Array.isArray(t.decisions) || typeof t.total !== "number") throw new Error("Données reçues inutilisables.");
      const open = t.decisions.filter((d) => d.follow_up && d.follow_up.status !== "done");
      update({ counts: { notes: t.total, decisions: t.decisions.length, open: open.length, owners: [...new Set(open.map((d) => d.follow_up.owner_name).filter(Boolean))], truncated: !!t.truncated } });
    }).catch(() => update({ counts: "unknown" }));
    if (user?.workspace_id) api.get("/workspaces").then(({ data: w }) => update({ workspace: w?.workspaces?.find((x) => x.id === user.workspace_id)?.name || "" })).catch(() => update({ workspace: "" }));
  };

  const confirmRemoval = async () => {
    const r = removal;
    setRemoval(null);
    if (!r || r.key !== detailRead.key || currentDetailKey.current !== r.key) return;
    const { key, snapshot } = r;
    try {
      await api.delete(`/saved/${encodeURIComponent(id)}`, { params: { expected_note_revision: snapshot.revisions.note, expected_tags_revision: snapshot.revisions.tags } });
      if (currentDetailKey.current !== key) return;
      setData((prev) => ({ ...prev, saved: false, saved_meta: null }));
      afterRemoval(key, snapshot);
      setSavedIssue(null);
      toast.success("Removed from saved");
    } catch (err) {
      failed(err, key, undefined, snapshot);
    }
  };

  // Edit of an existing reference: PATCH with the revision of the note this draft started from.
  const saveNote = async () => {
    if (!canEdit || !data || detailRead.status !== "success") return;
    const key = detailRead.key;
    if (!data.saved) return create(key);
    const sent = note;
    const sentSeq = noteDraft.seq;
    if (noteDraft.key !== key || !noteDraft.revision) {
      // The stored version this draft starts from is unknown (e.g. created, then rewritten before any read):
      // nothing is sent until an explicit re-read bases the draft on a known version.
      setSavedIssue({ key, sent, canReread: true, message: "La version enregistrée sur laquelle repose votre brouillon est inconnue : relisez la fiche avant d’enregistrer. Rien n’a été envoyé." });
      return;
    }
    try {
      const { data: answer } = await api.patch(`/saved/${encodeURIComponent(id)}`, { note: sent, expected_note_revision: noteDraft.revision });
      if (currentDetailKey.current !== key) return;
      setSavedIssue(null);
      // Stored, but its new revision is unknown (the note changed again since): the draft keeps its old
      // revision, so a later save of unsent changes meets a conflict instead of overwriting.
      if (!answer?.saved_meta) { confirmNote(key, sent, noteDraft.revision, sentSeq); detailRead.reload().catch(() => {}); return; }
      setData((prev) => ({ ...prev, saved: true, saved_meta: answer.saved_meta }));
      confirmNote(key, answer.saved_meta.note ?? sent, answer.saved_meta.revisions?.note, sentSeq);
      toast.success("Note updated");
    } catch (err) {
      failed(err, key, sent);
    }
  };

  // Explicit re-read: the editor keeps unsent changes (now based on the note read); a failure keeps all.
  const reread = async () => {
    const key = detailRead.key;
    const mark = (part) => setSavedIssue((prev) => (prev && prev.key === key ? { ...prev, ...part } : prev));
    mark({ rereading: true, rereadError: "" });
    rebaseOnRead.current = true;
    const pendingRemoval = savedIssueRef.current?.key === key ? savedIssueRef.current.removal : undefined;
    try {
      const fresh = await detailRead.reload();
      // After an uncertain removal, a re-read without reference shows that it happened (or another one did).
      if (pendingRemoval && fresh && !fresh.saved && currentDetailKey.current === key && hasCopy(pendingRemoval)) setLocalCopy({ key, note: pendingRemoval.note, tags: pendingRemoval.tags });
    } catch (err) {
      rebaseOnRead.current = false;
      mark({ rereading: false, rereadError: `La relecture a échoué (${formatError(err)}) : votre brouillon est conservé. Réessayez.` });
      return;
    }
    mark({ rereading: false, reread: true, canReread: false });
  };
  const issue = savedIssue?.key === detailRead.key ? savedIssue : null;
  const copy = localCopy?.key === detailRead.key ? localCopy : null;
  const dialog = removal?.key === detailRead.key ? removal : null;

  if (detailRead.status !== "success") return <div data-testid="video-detail-page"><Link to="/saved" className="text-primary text-sm">Retour à Saved</Link><ReadState read={detailRead} scope="video-detail" /></div>;
  const { video, creator, saved } = data;
  const original = originalVideoLink(video);

  const AuthorLink = creator?.is_followed === false ? "a" : Link;
  const authorLinkProps = creator?.is_followed === false ? { href: creator.profile_url, target: "_blank", rel: "noreferrer" } : { to: `/creators/${creator?.id}` };

  return (
    <div className="space-y-6" data-testid="video-detail-page" ref={page}>
      <button
        onClick={() => nav(-1)}
        className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        data-testid="video-back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <VideoAttribution video={video} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <VideoPreview video={video} />

          {creator && (
            <AuthorLink {...authorLinkProps} className="block">
              <Card className="p-3 flex items-center gap-3 hover:border-primary/60 transition-colors">
                <MediaImage src={creator.avatar} alt="" name={creator.name} kind="avatar" demo={!isLiveCreator(creator)} className="h-10 w-10 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{creator.name} <LiveDemoBadge creator={creator} /></div>
                  <div className="text-xs text-muted-foreground truncate">{creator.handle}</div>
                </div>
                <PlatformBadge platform={creator.platform} />
              </Card>
            </AuthorLink>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-display text-2xl font-bold leading-tight">{video.title}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Published {timeAgo(video.published_at)} • {video.duration_seconds}s • {video.topic}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {original && (
                <Button asChild variant="outline" data-testid="open-on-platform-button">
                  <a href={original.href} target="_blank" rel="noopener noreferrer" aria-label={`${original.label} (nouvel onglet)`}>
                    <ExternalLink className="h-4 w-4 mr-2" aria-hidden="true" /> {original.label}
                  </a>
                </Button>
              )}
            {canEdit && <Button
              ref={saveButton}
              onClick={toggleSave}
              variant={saved ? "default" : "outline"}
              disabled={!canEdit}
              title={canEdit ? undefined : "Viewers have read-only access in this workspace"}
              data-testid="save-video-bookmark-button"
            >
              {saved ? <BookmarkCheck className="h-4 w-4 mr-2" /> : <Bookmark className="h-4 w-4 mr-2" />}
              {saved ? "Saved" : "Save"}
            </Button>}
            </div>
          </div>
          <ConfirmAction open={!!dialog} onClose={() => setRemoval(null)} onConfirm={confirmRemoval} testid="saved-removal-dialog"
            contextKey={detailRead.key} restoreFocusFallback={removalFocusFallback}
            title="Retirer cette vidéo de Saved ?" confirmLabel="Retirer de Saved">
            {dialog && <RemovalText dialog={dialog} shared={!!user?.workspace_id} />}
          </ConfirmAction>
          {/* OP-036 review R3: the copy outlives a new save; its messages follow the confirmed current state (`saved`). */}
          {copy && !issue && <p className="text-xs text-muted-foreground" role="status" data-testid="note-local-copy-hint">{saved
            ? "La vidéo est de nouveau dans Saved, avec une nouvelle référence. La copie locale de la note lue avant le retrait reste dans « My Notes » ; elle n’y a pas été restaurée."
            : "Retirée de Saved. La dernière note lue est conservée dans « My Notes », en copie locale non enregistrée."}</p>}
          {issue && <div role="alert" className="rounded-lg border border-border p-3 space-y-2 text-sm" data-testid="saved-issue">
            <p data-testid="saved-issue-message">{issue.message}</p>
            {issue.reread && !saved && issue.removal && <p data-testid="saved-issue-removal-confirmed">La fiche relue n’a plus de référence Saved{copy ? " : la dernière note lue est conservée dans « My Notes », en copie locale non enregistrée." : "."}</p>}
            {issue.sent !== undefined && <>
              <label className="block text-xs font-medium" htmlFor="saved-issue-sent">Texte envoyé, non confirmé par le serveur</label>
              <Textarea id="saved-issue-sent" readOnly value={issue.sent} placeholder="(note vide)" className="min-h-16 text-xs" data-testid="saved-issue-sent" />
            </>}
            {issue.reread && <>
              <p data-testid="saved-issue-reread-done">Fiche relue. Votre brouillon courant reste dans l’éditeur{unsent ? " : « Save note » remplacera la note enregistrée ci-dessous." : "."}</p>
              <label className="block text-xs font-medium" htmlFor="saved-issue-server">{saved ? "Note actuellement enregistrée" : "Aucune référence enregistrée pour cette vidéo"}</label>
              {saved && <Textarea id="saved-issue-server" readOnly value={data.saved_meta?.note || ""} placeholder="(note vide)" className="min-h-16 text-xs" data-testid="saved-issue-server" />}
            </>}
            {issue.rereadError && <p className="text-destructive" data-testid="saved-issue-reread-error">{issue.rereadError}</p>}
            <div className="flex flex-wrap gap-2">
              {issue.canReread && <Button type="button" size="sm" variant="outline" disabled={issue.rereading} onClick={reread} data-testid="saved-issue-reread">{issue.rereading ? "Relecture…" : "Relire la fiche"}</Button>}
              <Button type="button" size="sm" variant="ghost" onClick={() => setSavedIssue(null)} data-testid="saved-issue-close">Fermer</Button>
            </div>
          </div>}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Views", value: formatNumber(video.views), icon: Eye },
              { label: "Likes", value: typeof video.likes === "number" ? formatNumber(video.likes) : "Indisponible", icon: Heart },
              { label: "Comments", value: typeof video.comments === "number" ? formatNumber(video.comments) : "Indisponible", icon: MessageCircle },
              { label: "Shares", value: video.platform === "youtube" && video.source === "live" ? "Indisponible" : typeof video.shares === "number" ? formatNumber(video.shares) : "Indisponible", icon: Share2 },
            ].map((m) => (
              <Card key={m.label} className="p-3">
                <div className="flex items-center gap-1.5 text-muted-foreground text-[10px] uppercase tracking-wider">
                  <m.icon className="h-3 w-3" />{m.label}
                </div>
                <div className="font-mono font-bold text-lg mt-1">{m.value}</div>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Card className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Engagement rate</div>
              <div className="font-mono font-bold text-lg mt-1">{video.engagement_rate}%</div>
            </Card>
            <Card className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Outlier multiplier</div>
              <div className="font-mono font-bold text-lg mt-1 text-[hsl(var(--emerald))]">{video.outlier_multiplier}x</div>
            </Card>
            <Card className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Baseline du profil de référence</div>
              <div className="font-mono font-bold text-lg mt-1">{formatNumber(video.creator_avg_views)}</div>
            </Card>
          </div>

          <Tabs defaultValue={searchParams.get("tab") === "thread" && user?.workspace_id ? "thread" : "analysis"} className="w-full">
            <TabsList className="flex h-auto w-full flex-wrap justify-start" data-testid="video-tabs">
              <TabsTrigger value="analysis" data-testid="tab-analysis"><Sparkles className="h-3.5 w-3.5 mr-1" />Analyse textuelle IA</TabsTrigger>
              <TabsTrigger value="transcript" data-testid="tab-transcript">Texte source</TabsTrigger>
              <TabsTrigger value="notes" data-testid="tab-notes">My Notes</TabsTrigger>
              {user?.workspace_id && (
                <TabsTrigger value="thread" data-testid="tab-thread" onClick={() => setUnread(0)}>
                  <MessagesSquare className="h-3.5 w-3.5 mr-1" />Team Thread
                  {commentCount > 0 && <span className="ml-1.5 rounded-full bg-primary/15 text-primary font-mono text-[10px] px-1.5 py-0.5" data-testid="tab-thread-count">{commentCount}</span>}
                  {unread > 0 && <span className="ml-1 h-2 w-2 rounded-full bg-[hsl(var(--rose))]" data-testid="tab-thread-unread-dot" />}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="analysis" className="mt-4 space-y-3">
              <AnalysisEvidenceNotice analysis={analysis} />
              <AIState read={ai.read} scope="video" />
              {!analysis ? (
                <Card className="p-8 text-center" role="status">
                  <Sparkles className="h-8 w-8 text-primary mx-auto mb-2" />
                  <h4 className="font-display font-bold text-lg">Aucune analyse disponible</h4>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                    Les comptes autorisés peuvent demander une interprétation du texte et des métriques disponibles, sans analyse de la vidéo ni de l’audio.
                  </p>
                  {canEdit && <Button
                    onClick={runAnalysis}
                    disabled={analyzing || !ai.allowed}
                    className="mt-4"
                    data-testid="run-analysis-button"
                  >
                    {analyzing ? "Analyse en cours…" : "Analyser le texte disponible"}
                  </Button>}
                </Card>
              ) : (
                <div className="space-y-3">
                  {analysisAuto && (
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold px-2.5 py-1" data-testid="auto-analyzed-badge">
                      <Sparkles className="h-3 w-3" /> Auto-analyzed when this breakout was detected
                    </div>
                  )}
                  <VideoAnalysis analysis={analysis} />
                </div>
              )}
            </TabsContent>

            <TabsContent value="transcript" className="mt-4">
              <Card className="p-5 space-y-3">
                <h4 className="font-semibold">{video.transcript_source === "caption" ? "Description / caption fournie" : "Texte fourni (provenance non vérifiée)"}</h4>
                <p className="text-sm text-muted-foreground" data-testid="video-source-text-notice">Ce texte n’est pas une transcription parlée vérifiée.</p>
                <pre data-testid="video-source-text" className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-muted-foreground">
                  {typeof video.transcript === "string" && video.transcript.trim() && !video.transcript.startsWith("(No caption or transcript available") ? video.transcript : "Aucun texte source disponible."}
                </pre>
              </Card>
            </TabsContent>

            <TabsContent value="notes" className="mt-4">
              <Card className="p-5 space-y-3">
                {canEdit ? <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What did you learn from this video? Ideas to try…"
                  className="min-h-32"
                  data-testid="video-note-input"
                /> : <p className="whitespace-pre-wrap text-sm">{note || "Aucune note sauvegardée."}</p>}
                {canEdit && unsent && <p className="text-xs text-muted-foreground" data-testid="note-unsent">Modifications non enregistrées.</p>}
                {canEdit && <Button onClick={saveNote} data-testid="save-note-button">Save note</Button>}
                {copy && <div role="note" className="rounded-lg border border-dashed border-border p-3 space-y-2" data-testid="note-local-copy">
                  <label className="block text-xs font-semibold" htmlFor="note-local-copy-text">Last note read — unsaved local copy</label>
                  <p className="text-xs text-muted-foreground" data-testid="note-local-copy-status">{saved
                    ? "Copie historique, lue avant le retrait. La vidéo est de nouveau dans Saved avec une nouvelle référence ; cette copie n’y a pas été restaurée. Elle n’est enregistrée nulle part, n’est jamais renvoyée et disparaît en quittant la page."
                    : "La référence Saved n’existe plus. Cette copie est la dernière version lue par cette page ; elle n’est enregistrée nulle part, n’est jamais renvoyée et disparaît en quittant la page."}</p>
                  <Textarea id="note-local-copy-text" readOnly value={copy.note} placeholder="(note vide)" className="min-h-16 text-xs" data-testid="note-local-copy-text" />
                  {copy.tags.length > 0 && <p className="text-xs" data-testid="note-local-copy-tags">Tags lus : {copy.tags.join(", ")}</p>}
                  <div className="flex flex-wrap gap-2">
                    {canEdit && <Button type="button" size="sm" variant="outline" onClick={() => setNote(copy.note)} data-testid="note-local-copy-to-editor">Copier dans l’éditeur</Button>}
                    <Button type="button" size="sm" variant="ghost" onClick={() => setLocalCopy(null)} data-testid="note-local-copy-dismiss">Masquer la copie</Button>
                  </div>
                </div>}
              </Card>
            </TabsContent>

            {user?.workspace_id && (
              <TabsContent value="thread" className="mt-4">
                <CommentThread key={`${id}-${user?.workspace_id || "personal"}`} videoId={id} saved={saved} onCountChange={updateCommentCount} />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
}

// OP-036: what removing the reference destroys (for every member in a workspace) and what stays reachable.
function RemovalText({ dialog, shared }) {
  const { snapshot, counts, workspace } = dialog;
  const tags = snapshot.tags.length ? snapshot.tags.map((t) => `« ${t} »`).join(", ") : "aucun tag";
  const by = snapshot.savedBy ? (snapshot.savedBasis === "creation" ? `enregistrée par ${snapshot.savedBy}` : `dernier enregistrement connu par ${snapshot.savedBy}`) : "";
  const meta = [by, snapshot.editedBy ? `modifiée par ${snapshot.editedBy}` : ""].filter(Boolean).join(", ");
  const space = workspace === "loading" ? "cet espace" : workspace ? `l’espace « ${workspace} »` : "cet espace";
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  return <>
    {shared ? <p data-testid="saved-removal-dialog-shared">Cette référence est partagée par {space}. La retirer supprimera <b>pour tous les membres</b> et <b>définitivement</b> la note Saved{meta ? ` (${meta})` : ""} et ses tags ({tags}). Ils ne pourront pas être restaurés.</p>
      : <p data-testid="saved-removal-dialog-personal">La retirer supprimera <b>définitivement</b> votre note et vos tags ({tags}). Ils ne pourront pas être restaurés.</p>}
    <p data-testid="saved-removal-dialog-note">Note : {snapshot.note ? `« ${snapshot.note.length > 200 ? `${snapshot.note.slice(0, 200)}…` : snapshot.note} »` : "(vide)"}</p>
    <p data-testid="saved-removal-dialog-kept">{counts === "loading" ? "Comptage des notes, décisions et suivis en cours…"
      : counts === "unknown" ? "Nombre de notes, décisions et suivis : inconnu (la relecture du fil a échoué). Ce retrait ne les supprime pas."
      : `Resteront accessibles, avec la mention « Removed from Saved » : ${counts.truncated ? "au moins " : ""}${plural(counts.notes, "note", "notes")}, ${plural(counts.decisions, "décision", "décisions")}, ${plural(counts.open, "suivi ouvert", "suivis ouverts")}${counts.owners.length ? ` (responsables : ${counts.owners.join(", ")})` : ""}, dans le fil, le tableau Décisions et Mes suivis.`}</p>
  </>;
}
